import React, { forwardRef, useEffect, useState } from 'react';
import {
  Dialog,
  Box,
  Avatar,
  Typography,
  IconButton,
  DialogProps,
  Divider,
  Slide,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import { UserAvatar } from '../AssignTask/AssignTask';
import { TransitionProps } from '@mui/material/transitions';
import { AssignmentStatus, ScreeningStatus, StatusChip } from './CandidateContent.styles';
import ViewResumeButton from './ViewResumeButton';
import ActivityTimeline from './ActivityHistory';
import { getCandidateStatusHistory } from '../candidateManagementApi';
import { useParams } from 'react-router-dom';
import { theme } from '../../../../theme';
import { statusColorMap } from '../../JobManagement/CreateJobPosting/CreateJob/ReadJob';
import { toTitleCase } from './CandidateContent';

const Transition = forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="left" ref={ref} {...props} />;
});

interface CandidateProfileProps extends DialogProps {
  open: boolean;
  onClose: () => void;
  candidateName: string;
  candidateEmail: string;
  recruiterName: string;
  candidatePosition: string;
  candidateStatus: string;
  candidatePhoneNumber: string | null;
  candidateMapID: number | null;
}

export interface CandidateStatusHistory {
  StatusID: number;
  Status: string;
  UpdatedBy: number;
  UpdatedDate: string
}

const CandidateProfile: React.FC<CandidateProfileProps> = ({
  open,
  onClose,
  candidateName,
  candidateEmail,
  recruiterName,
  candidatePosition,
  candidateStatus,
  candidatePhoneNumber,
  candidateMapID,
}) => {
  const { tenant } = useParams<{ tenant: string }>();
  const [statusHistory, setStatusHistory] = useState<CandidateStatusHistory[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchCandidateStatusHistory = async () => {
    try {
      setLoading(true)
      const response = await getCandidateStatusHistory(tenant, { "MapID": candidateMapID });
      setStatusHistory(response || []);
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false);
    }

  }

  useEffect(() => {
    if (open && candidateMapID) {
      fetchCandidateStatusHistory();
    }
  }, [open, candidateMapID]);

  const statusStyles = statusColorMap[candidateStatus] || {
    color: "#1565C0",
    bgColor: "#E3F2FD",
    borderColor: "#BBDEFB",
  };

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      TransitionComponent={Transition}
      fullScreen
      BackdropProps={{ style: { backgroundColor: "rgba(0,0,0,0.5)",} }}
      PaperProps={{
        sx: {
          maxWidth: '22vw',
          height: '100vh',
          margin: 0,
          overflow: 'hidden',
          position: 'absolute',
          right: 0,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" p={2} mb='1.6vw'>
        <Box display="flex" alignItems="center">
          <IconButton
            sx={{
              backgroundColor: theme.palette.primary.light,
              color: theme.palette.primary.main,
              mr: 1
            }}
          >
            <PersonIcon />
          </IconButton>
          <Box>
            <Typography variant="h6" fontWeight="600">
              Candidate Info
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {toTitleCase(candidateName)}
            </Typography>
          </Box>
        </Box>
        <IconButton
          edge="start"
          color="inherit"
          onClick={onClose}
          aria-label="close"
          sx={{ alignSelf: 'flex-start', mt: 0, ml: 2 }}
        >
          <CloseIcon />
        </IconButton>
      </Box>




      <Divider sx={{marginBottom:'1.6vw'}} />

      {/* Scrollable content */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          px: 2,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.3)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          scrollbarWidth: 'thin', // For Firefox
          scrollbarColor: 'rgba(0,0,0,0.3) transparent',
        }}
        className="scroll-container"
      >
        {/* Profile Info */}
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          mb={'1.66vw'}
          gap={1}
        >
          <UserAvatar width="3vw" height="3vw">{candidateName[0]}</UserAvatar>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6">{candidateName}</Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                wordBreak: 'break-all',
                whiteSpace: 'normal',
                overflowWrap: 'break-word',
                maxWidth: '100%',
              }}
            >
              {candidateEmail}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: '1.6vw' }} />

        {/* Application Details */}
        <Box mb={'1.6vw'}>
          <Box display="flex" alignItems="center" mb={2} gap={1.5}>
            <Typography variant="subtitle2" fontWeight={600}>
              Application Details
            </Typography>
            <StatusChip
              bg={statusStyles.bgColor}
              border={statusStyles.borderColor}
              color={statusStyles.color}
            >
              ● {candidateStatus}
            </StatusChip>
          </Box>

          {[
            { label: 'Phone number', value: candidatePhoneNumber || 'N/A' },
            { label: 'Recruiter Assigned', value: recruiterName },
            { label: 'Applied Position', value: candidatePosition },
          ].map(({ label, value }) => (
            <Box key={label} display="flex" mb={'0.8vw'} gap={1.5}>
              <Box width="50%">
                <Typography variant="body2" color="text.secondary">
                  {label}
                </Typography>
              </Box>
              <Box width="50%">
                <Typography variant="body2">{value}</Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <ViewResumeButton candidateMapID={candidateMapID || 0} />

        <Box mb='1.6vw'>
          <ActivityTimeline statusHistory={statusHistory} loading={loading} />
        </Box>
      </Box>
    </Dialog>
  );
};

export default CandidateProfile;
